"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { FiRefreshCw, FiSend } from "react-icons/fi";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import type { EmailTopLevelNode } from "@/components/email-composer/types/tiptap-json";
import PatientEmailComposeDrawer from "@/components/patient/patient-email-compose-drawer";
import Button from "@/components/ui/button";
import ConnectionCard from "@/components/wearables/connection-card";
import SendConnectLinkDialog from "@/components/wearables/send-connect-link-dialog";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { formatWearableProvider } from "@/lib/wearable-connect";
import type { PatientSummary } from "@/types/patient";
import type { WearableConnection } from "@/types/wearable";
import { useDisconnectPatientWearableMutation, usePatientWearableConnections, useSyncPatientWearableExportsMutation } from "@/query/patient/wearables";

/**
 * The Devices section on a patient's chart.
 *
 * Deliberately the same card and status vocabulary the patient sees in their
 * portal. A device that reads "Access turned off in the device's app" to the
 * patient has to read the same to their clinician — the whole point of surfacing
 * a degraded connection is that the two people looking at it agree about what is
 * wrong.
 *
 * What the chart deliberately does NOT carry is a way to authorise a device.
 * Connecting one starts a continuous flow of somebody's biometrics, and the
 * consent for that belongs to the patient, not to whoever has their chart open —
 * so the only route from here is Send link, which the patient completes on their
 * own phone. Disconnect stays, because it only ever stops collection, and it is
 * the sole way a clinic can honour "stop collecting my data" said over the phone.
 */
export function useWearableDevices(patientId, enabled) {
  const $ = _c(12);
  const {
    data: connections
  } = usePatientWearableConnections(patientId, enabled);
  const disconnect = useDisconnectPatientWearableMutation(patientId);
  const sync = useSyncPatientWearableExportsMutation(patientId);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  let t0;
  if ($[0] !== disconnect || $[1] !== disconnectTarget) {
    t0 = async () => {
      if (!disconnectTarget) {
        return;
      }
      try {
        await disconnect.mutateAsync(disconnectTarget.id);
        toast({
          title: "Device disconnected",
          variant: "success"
        });
      } catch {
        toast({
          title: "Couldn't disconnect the device",
          variant: "error"
        });
      }
    };
    $[0] = disconnect;
    $[1] = disconnectTarget;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleDisconnect = t0;
  let t1;
  if ($[3] !== sync) {
    t1 = async () => {
      try {
        await sync.mutateAsync();
        toast({
          title: "Readings imported",
          variant: "success"
        });
      } catch {
        toast({
          title: "Couldn't import readings",
          variant: "error"
        });
      }
    };
    $[3] = sync;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleSync = t1;
  let t2;
  if ($[5] !== connections || $[6] !== disconnect.isPending || $[7] !== disconnectTarget || $[8] !== handleDisconnect || $[9] !== handleSync || $[10] !== sync.isPending) {
    t2 = {
      connections,
      isDisconnecting: disconnect.isPending,
      isSyncing: sync.isPending,
      disconnectTarget,
      setDisconnectTarget,
      handleDisconnect,
      handleSync
    };
    $[5] = connections;
    $[6] = disconnect.isPending;
    $[7] = disconnectTarget;
    $[8] = handleDisconnect;
    $[9] = handleSync;
    $[10] = sync.isPending;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
type WearableDevices = ReturnType<typeof useWearableDevices>;

/**
 * Hands the connect flow to the patient instead of driving it here.
 *
 * Takes the patient rather than the devices bundle: nothing about drafting a
 * message needs the connection list, and the message needs a name, a phone
 * number and an email that the bundle does not carry.
 */
export function WearableSendLinkButton(t0) {
  const $ = _c(10);
  const {
    patient
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [emailBody, setEmailBody] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Button size="sm" variant="secondary" onClick={() => setIsOpen(true)}><FiSend className="mr-1 h-3 w-3" />Send link</Button>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isOpen || $[2] !== patient) {
    t2 = isOpen && <SendConnectLinkDialog patient={patient} open={isOpen} onOpenChange={setIsOpen} onComposeEmail={setEmailBody} />;
    $[1] = isOpen;
    $[2] = patient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== emailBody || $[5] !== patient) {
    t3 = emailBody && patient.email && <PatientEmailComposeDrawer open={true} onOpenChange={next => {
      if (!next) {
        setEmailBody(null);
      }
    }} recipientEmail={patient.email} patientId={patient.id} initialSubject="Connect your device" initialBodyNodes={emailBody} />;
    $[4] = emailBody;
    $[5] = patient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function WearableSyncButton(t0) {
  const $ = _c(8);
  const {
    devices
  } = t0;
  if (!devices.connections || devices.connections.length === 0) {
    return null;
  }
  const t1 = devices.handleSync;
  const t2 = devices.isSyncing;
  const t3 = devices.isSyncing && "animate-spin";
  let t4;
  if ($[0] !== t3) {
    t4 = cx("mr-1 h-3 w-3", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t4) {
    t5 = <FiRefreshCw className={t4} />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== devices.handleSync || $[5] !== devices.isSyncing || $[6] !== t5) {
    t6 = <Button size="sm" variant="secondary" onClick={t1} disabled={t2}>{t5}Sync</Button>;
    $[4] = devices.handleSync;
    $[5] = devices.isSyncing;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
export function WearableDeviceList(t0) {
  const $ = _c(18);
  const {
    devices
  } = t0;
  const {
    connections,
    isDisconnecting,
    disconnectTarget,
    setDisconnectTarget,
    handleDisconnect
  } = devices;
  if (!connections || connections.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== connections || $[1] !== setDisconnectTarget) {
    let t2;
    if ($[3] !== setDisconnectTarget) {
      t2 = connection => <ConnectionCard key={connection.id} connection={connection} actions={connection.status === "connected" ? <Button size="sm" variant="ghost" onClick={() => setDisconnectTarget(connection)}>Disconnect</Button> : null} />;
      $[3] = setDisconnectTarget;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = connections.map(t2);
    $[0] = connections;
    $[1] = setDisconnectTarget;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !!disconnectTarget;
  let t3;
  if ($[5] !== setDisconnectTarget) {
    t3 = open => {
      if (!open) {
        setDisconnectTarget(null);
      }
    };
    $[5] = setDisconnectTarget;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = disconnectTarget?.provider ?? null;
  let t5;
  if ($[7] !== t4) {
    t5 = formatWearableProvider(t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `Disconnect ${t5}?`;
  let t7;
  if ($[9] !== handleDisconnect || $[10] !== isDisconnecting || $[11] !== t2 || $[12] !== t3 || $[13] !== t6) {
    t7 = <DeleteConfirmationModal isOpen={t2} setIsOpen={t3} onConfirm={handleDisconnect} isLoading={isDisconnecting} confirmText="Disconnect" title={t6} subtitle="New readings will stop arriving. Readings already on this chart stay there." />;
    $[9] = handleDisconnect;
    $[10] = isDisconnecting;
    $[11] = t2;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t7) {
    t8 = <div className="space-y-2">{t1}{t7}</div>;
    $[15] = t1;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}